import * as yup from 'yup';
import { extend } from 'vee-validate';

let acceptedRuleRegistered = false;

/**
 * Register checkbox "must be checked" rule once.
 * vee-validate's built-in `required` treats boolean false as valid.
 */
export function ensureAcceptedRule() {
  if (acceptedRuleRegistered) return;
  extend('accepted', {
    validate(value) {
      return value === true;
    },
    message: 'This field is required',
  });
  acceptedRuleRegistered = true;
}

ensureAcceptedRule();

/**
 * Build vee-validate rules string from InputConfig.
 * @param {import('./formTypes').InputConfig} input
 * @returns {string|Object}
 */
export function buildRulesString(input) {
  const config = input.rules && typeof input.rules === 'object' ? input.rules : {};
  const parts = [];

  if (config.required || input.required) {
    if (input.type === 'multi-select') {
      parts.push('array_required');
    } else if (input.type === 'checkbox') {
      // Boolean false is not "empty" for vee-validate's required rule.
      parts.push('accepted');
    } else {
      parts.push('required');
    }
  }
  if (input.type === 'email' || config.email) {
    parts.push('email');
  }
  if (config.min !== undefined) {
    parts.push(`min:${config.min}`);
  }
  if (config.max !== undefined) {
    parts.push(`max:${config.max}`);
  }
  if (config.numeric) {
    parts.push('numeric');
  }

  return parts.join('|');
}

/**
 * Collect field names from inputArr schema (stable identity for reset).
 * @param {import('./formTypes').InputGroup[]} inputArr
 * @returns {string[]}
 */
export function getInputFieldNames(inputArr) {
  const names = [];
  (inputArr || []).forEach((group) => {
    (group.render || []).forEach((input) => {
      if (input?.name) names.push(input.name);
    });
  });
  return names;
}

/**
 * Collect default values from inputArr schema.
 * @param {import('./formTypes').InputGroup[]} inputArr
 * @param {Object} [overrides]
 */
export function getInitialFormValues(inputArr, overrides = {}) {
  const values = { ...overrides };

  (inputArr || []).forEach((group) => {
    (group.render || []).forEach((input) => {
      if (!input.name) return;
      if (values[input.name] !== undefined) return;

      if (input.defaultValue !== undefined) {
        values[input.name] = input.defaultValue;
      } else if (input.type === 'checkbox') {
        values[input.name] = false;
      } else if (input.type === 'multi-select') {
        values[input.name] = [];
      } else {
        values[input.name] = '';
      }
    });
  });

  return values;
}

/**
 * Build a Yup schema from inputArr for optional whole-form validation.
 * @param {import('./formTypes').InputGroup[]} inputArr
 */
export function buildYupSchema(inputArr) {
  const shape = {};

  (inputArr || []).forEach((group) => {
    (group.render || []).forEach((input) => {
      if (!input.name) return;

      const config = input.rules && typeof input.rules === 'object' ? input.rules : {};
      let field;

      if (input.type === 'number') {
        field = yup.number().typeError(`${input.label || input.name} must be a number`);
      } else if (input.type === 'checkbox') {
        field = yup.boolean();
      } else if (input.type === 'multi-select') {
        field = yup.array();
      } else {
        field = yup.string();
      }

      if (config.required || input.required) {
        if (input.type === 'multi-select') {
          field = field.min(1, `${input.label || input.name} is required`);
        } else if (input.type === 'checkbox') {
          field = field.oneOf(
            [true],
            `${input.label || input.name} is required`,
          );
        } else {
          field = field.required(`${input.label || input.name} is required`);
        }
      }
      if (input.type === 'email' || config.email) {
        field = field.email('Enter a valid email address');
      }
      if (config.min !== undefined && input.type !== 'number') {
        field = field.min(config.min, `Minimum ${config.min} characters`);
      }
      if (config.max !== undefined && input.type !== 'number') {
        field = field.max(config.max, `Maximum ${config.max} characters`);
      }

      shape[input.name] = field;
    });
  });

  return yup.object().shape(shape);
}

export default {
  buildRulesString,
  getInitialFormValues,
  buildYupSchema,
  ensureAcceptedRule,
};
